import {useTranslation} from 'react-i18next';
import {useTheme} from 'styled-components';
import {Offer, PriceItem, OFFER_CONFIRMATION_TYPES} from '@chekin/core';
import {CustomIcon} from '@guestapp/old-ui-kit';
import {usePaymentSettings} from 'hooks';
import {isTimeBasedOffer} from 'utils/upselling';

import {
  Title,
  Container,
  TotalPrice,
  InfoContainer,
  CounterWrapper,
  TotalPriceWrapper,
} from './styled';
import {TimeBasedCounter} from './TimeBasedCounter';
import {PriceCounter} from './PriceCounter';

const getTitleKey = (unitType: PriceItem['unit_type']) =>
  `upselling_add_to_card_form.title.${unitType?.toLocaleLowerCase() || 'per_unit'}`;

const formatPrice = (currencySymbol: string, price: number) =>
  `${currencySymbol}${price.toFixed(2)}`;

type AddToCartFormProps = {
  items?: Offer['items'];
  itemQuantities: Record<string, number>;
  onItemChange: (itemId: string, value: number) => void;
  confirmationType?: OFFER_CONFIRMATION_TYPES;
  category?: Offer['category'];
  template?: string;
  className?: string;
};

export const AddToCartForm = ({
  items = [],
  itemQuantities,
  onItemChange,
  confirmationType,
  category,
  template,
  className,
}: AddToCartFormProps) => {
  const {t} = useTranslation();
  const {currencySymbol} = usePaymentSettings();
  const theme = useTheme();

  const totalPrice = items.reduce(
    (sum, item) => sum + Number(item.price) * (itemQuantities[item.id] ?? 0),
    0,
  );

  const isTimeBasedCategory = isTimeBasedOffer({category});

  return (
    <Container className={className}>
      <CounterWrapper>
        {isTimeBasedCategory ? (
          <TimeBasedCounter
            items={items}
            onItemChange={onItemChange}
            template={template}
          />
        ) : (
          <>
            <Title>
              <CustomIcon
                name="experiencesUnits"
                color={theme.hasTemplate ? theme.colors.branding.icons : '#777E91'}
                size={24}
              />
              <span>{t(getTitleKey(items[0]?.unit_type))}</span>
            </Title>

            {items.map(priceItem => (
              <PriceCounter
                key={priceItem.id}
                value={itemQuantities[priceItem.id] ?? 0}
                price={Number(priceItem.price)}
                unitType={priceItem.unit_type}
                onChange={(v: number) => onItemChange(priceItem.id, v)}
              />
            ))}
          </>
        )}
      </CounterWrapper>
      <TotalPriceWrapper>
        <TotalPrice>
          <span>{t('upselling_add_to_card_form.total_price')}</span>
          <span className="text-xl">{formatPrice(currencySymbol, totalPrice)}</span>
        </TotalPrice>
        {confirmationType === OFFER_CONFIRMATION_TYPES.manual && (
          <InfoContainer>
            <CustomIcon
              name="informationGray"
              size={24}
              custom
              color={theme.colors.branding.fontSecondary}
            />
            <span>{t('upselling_add_to_card_form.total_price_info')}</span>
          </InfoContainer>
        )}
      </TotalPriceWrapper>
    </Container>
  );
};
